import React, { useState, useEffect, useCallback, useMemo, useRef, forwardRef, useImperativeHandle } from 'react';
import { FileGrid } from './FileGrid';
import { FileList } from './FileList';
import { Pagination } from './Pagination';
import { Grid, List, FolderPlus, Upload } from 'lucide-react';
import { fetchFiles, deleteSingleFile, searchFiles } from '../../services/assetManagerService';
import { FileDetails } from './FileDetails';
import { SearchBar, SearchBarRef } from './SearchBar';
import LoadingSpinner from '@/components/common/LoadingSpinner';

import { useWebSocketContext } from '../../contexts/WebSocketContext';
import { WebSocketHandlers } from '../../hooks/websocket/types';


export interface Asset {
  id?: string;
  originalFile: string;
  filename: string;
  size: number;
  mimeType: string;
  thumnailImage?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface FileExplorerProps {
  viewMode: 'grid' | 'list';
  onViewModeChange: (mode: 'grid' | 'list') => void;
  currentFolder: string;
  onCreateFolder: () => void;
  allowedFileTypes?: string[];
  multiSelect?: boolean;
  isPicker?: boolean;
  selectedAssets: Asset[];
  setSelectedAssets: (assets: Asset[]) => void;
  onNavigate: (folder: string) => void;
  setIsUploadModalOpen: (open: boolean) => void;
  setIsNavigating: (loading: boolean) => void;
  fileChangesInSize: () => void;
}

export const FileExplorer = forwardRef<{ clearSearch: () => void }, FileExplorerProps>(({
  viewMode,
  onViewModeChange,
  currentFolder,
  onCreateFolder,
  allowedFileTypes,
  multiSelect = true,
  isPicker = false,
  selectedAssets,
  setSelectedAssets,
  onNavigate,
  setIsUploadModalOpen,
  setIsNavigating,
  fileChangesInSize
}, ref) => {
  const searchBarRef = useRef<SearchBarRef>(null);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  const { registerHandler, unregisterHandler } = useWebSocketContext();

  // Add loadFilesRef
  const loadFilesRef = useRef<(() => Promise<void>) | null>(null);

  const loadFiles = useCallback(async () => {
    setIsSearching(true);
    try {
      if (debouncedSearchQuery.trim()) {
        const response = await searchFiles(debouncedSearchQuery, currentPage);
        setAssets(response.files);
        setTotalPages(response.totalPages);
      } else {
        setIsNavigating(true);
        const response = await fetchFiles(currentFolder, currentPage);
        setAssets(response.files);
        setTotalPages(response.totalPages);
      }
    } catch (error) {
      console.error('Error fetching files:', error);
    } finally {
      setIsSearching(false);
      setIsNavigating(false);
    }
  }, [debouncedSearchQuery, currentFolder, currentPage, setIsNavigating]);

  // Update ref when loadFiles changes
  useEffect(() => {
    loadFilesRef.current = loadFiles;
  }, [loadFiles]);

  // Update WebSocket handler to use ref
  const handler = useMemo<WebSocketHandlers>(() => ({
    onFileChange: async () => {
      if (loadFilesRef.current) {
        loadFilesRef.current();
      }
    },
  }), []); // Empty dependency array

  // Register and unregister handler
  useEffect(() => {
    registerHandler(handler);
    return () => unregisterHandler(handler);
  }, [handler, registerHandler, unregisterHandler]);

  // Reset selectedAssets when currentFolder changes
  useEffect(() => {
    setSelectedAssets([]);
  }, [currentFolder, setSelectedAssets]);

  // Initial load and folder/page changes
  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  useImperativeHandle(ref, () => ({
    clearSearch: () => {
      searchBarRef.current?.clearSearch();
      setDebouncedSearchQuery('');
    }
  }));

  const resetSearch = useCallback(() => {
    setDebouncedSearchQuery('');
    setCurrentPage(0);
  }, []);

  const handleSearchSubmit = useCallback((query: string) => {
    setDebouncedSearchQuery(query);
    setCurrentPage(0);
  }, []);

  const handleFolderNavigation = useCallback((folder: string) => {
    // Clear search when navigating
    searchBarRef.current?.clearSearch();
    // Reset search query
    setDebouncedSearchQuery('');
    // Navigate to folder
    onNavigate(folder);
  }, [onNavigate]);

  const handlePageChange = useCallback((newPage: number) => {
    setCurrentPage(newPage);
  }, []);

  const handleAssetSelect = useCallback((asset: Asset) => {
    const isSelected = selectedAssets.some((a) => a.id === asset.id);
    if (multiSelect) {
      if (isSelected) {
        setSelectedAssets(selectedAssets.filter((a) => a.id !== asset.id));
      } else {
        setSelectedAssets([...selectedAssets, asset]);
      }
    } else {
      setSelectedAssets(isSelected ? [] : [asset]);
    }
  }, [multiSelect, selectedAssets, setSelectedAssets]);

  const handleDeleteSelected = useCallback(async () => {
    try {
      if (selectedAsset?.id) {
        await deleteSingleFile(selectedAsset.id);
      } else if (selectedAssets.length > 0) {
        await Promise.all(
          selectedAssets.map((asset) => deleteSingleFile(asset.id || ''))
        );
      }
      setSelectedAssets([]);
      setSelectedAsset(null)
      await loadFiles();
    } catch (error) {
      console.error('Error deleting selected files:', error);
    }
  }, [selectedAssets, selectedAsset, loadFiles, setSelectedAssets]);

  useEffect(() => {
    fileChangesInSize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assets]);

  return (
    <div>
      

      <div className="mb-4 sm:mb-6 bg-primary-50 p-4 sm:p-6 rounded-xl bg-white">
      
      <div className='flex flex-col sm:flex-row justify-between flex-start sm:items-center gap-4'>
        <div className="flex items-center space-x-2">
          {!isPicker && (
            <>
              {selectedAssets.length === 0 ? (
                <button
                  onClick={() => setSelectedAssets(assets)}
                  className="px-3 py-2 text-sm bg-primary-100 hover:bg-primary-200 text-blue-500 rounded-md transition duration-200"
                >
                  Select All
                </button>
              ) : (
                <button
                  onClick={() => setSelectedAssets([])}
                  className="px-3 py-2 text-sm bg-primary-100 hover:bg-primary-200 text-blue-500 rounded-md transition duration-200"
                >
                  Deselect All
                </button>
              )}
            </>
          )}
          {/* <div className="border-l border-primary-200 h-6 mx-2"></div> */}
          <button
            className={`p-2 rounded-md transition ${
              viewMode === 'grid'
                ? 'bg-primary-100 text-primary-600'
                : 'hover:bg-primary-100 text-primary-400'
            }`}
            onClick={() => onViewModeChange('grid')}
            aria-label="Grid view"
          >
            <Grid size={20} />
          </button>
          <button
            className={`p-2 rounded-md transition ${
              viewMode === 'list'
                ? 'bg-primary-100 text-primary-600'
                : 'hover:bg-primary-100 text-primary-400'
            }`}
            onClick={() => onViewModeChange('list')}
            aria-label="List view"
          >
            <List size={20} />
          </button>
        </div>
        <div className="flex flex-col gap-4 sm:flex-row items-start sm:items-center">
          <SearchBar 
            ref={searchBarRef}
            onSearch={handleSearchSubmit}
            onReset={resetSearch}
            isSearching={isSearching}
          />
          {!isPicker && (
            <>
              <button
                onClick={() => setIsUploadModalOpen(true)}
                className="flex items-center px-4 py-2 text-primary-600 hover:bg-primary-100 rounded-md border border-primary-200 transition duration-200 w-full sm:w-fit justify-center"
              >
                <Upload className="mr-2" size={18} />
                <span>Upload Files</span>
              </button>
              <button
                onClick={onCreateFolder}
                className="flex items-center px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 transition duration-200 w-full sm:w-fit justify-center"
              >
                <FolderPlus className="mr-2" size={18} />
                <span>New Folder</span>
              </button>
            </>
          )}
        </div>
        </div>
        {!isPicker && selectedAssets.length > 0 && (
        <div className="flex justify-between items-center border-t border-gray-100 mt-4 pt-4 ">
          <p className="text-black font-medium">{selectedAssets.length} file(s) selected</p>
          <button
            onClick={handleDeleteSelected}
            className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 transition duration-200"
          >
            Delete Selected
          </button>
        </div>
      )}
      </div>

      {isSearching ? (
        <div className="flex flex-col items-center justify-center h-64">
          <LoadingSpinner />
        </div>
      ) : assets.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64">
          <div className="text-center text-gray-500 flex flex-col items-center">
            {debouncedSearchQuery.trim() ? (
              // No search results message
              <>
                <p className="text-lg font-medium">No matching files found</p>
                <p className="mt-2">Try adjusting your search terms or browse all files</p>
              </>
            ) : (
              // Default empty state
              <>
                <p className="text-lg font-medium">No assets are available</p>
                {!isPicker && (
                  <>
                    <p className="mt-2">You can upload files or create a new folder.</p>
                    <div className="mt-4 flex justify-center space-x-2">
                      <button
                        onClick={() => setIsUploadModalOpen(true)}
                        className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 transition duration-200"
                      >
                        Upload Files
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      ) : viewMode === 'grid' ? (
        <FileGrid
          assets={assets}
          selectedAssets={selectedAssets}
          onAssetSelect={handleAssetSelect}
          onViewDetails={setSelectedAsset}
          allowedFileTypes={allowedFileTypes}
          isPicker={isPicker}
          multiSelect={multiSelect}
          onNavigate={handleFolderNavigation}
        />
      ) : (
        <FileList
          assets={assets}
          selectedAssets={selectedAssets}
          onAssetSelect={handleAssetSelect}
          onViewDetails={setSelectedAsset}
          allowedFileTypes={allowedFileTypes}
          isPicker={isPicker}
          multiSelect={multiSelect}
          onNavigate={handleFolderNavigation}
        />
      )}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}
      {!isPicker && selectedAsset && (
        <FileDetails
          asset={selectedAsset}
          onClose={() => setSelectedAsset(null)}
          onDelete={handleDeleteSelected}
        />
      )}
    </div>
  );
});